import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { useRecoilState } from 'recoil'
import {
    ContentAction,
    ContentActionView,
    ContentView,
    ProfileViewWithShadowBan
} from '../../api/client'
import { openConversationContentIdState } from '../../store/ConversationStore'
import { getPlatformDisplayName } from '../../util/types'
import { isActionApplied } from '../content/content-card/actions'
import { buildThread, ThreadNode } from './buildThread'
import ConversationPlaceholder from './ConversationPlaceholder'
import ConversationPostHeader from './ConversationPostHeader'
import ConversationThread from './ConversationThread'
import { useCommentThread } from './useCommentThread'

type Props = {
    onContentAction: (actions: ContentActionView[], queued?: boolean) => void
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
    onMarkAsReadClick: (content: ContentView) => void
    onMarkAsUnreadClick: (content: ContentView) => void
}

/**
 * Right-hand drawer showing a comment's post, parent and replies.
 * Opened by setting openConversationContentIdState from any card.
 */
export default function ConversationDrawer(props: Props) {
    const [openId, setOpenId] = useRecoilState(openConversationContentIdState)
    const { thread, state, refresh, refreshing, refreshError, notices } = useCommentThread(openId)
    const open = openId !== null
    const close = () => setOpenId(null)

    // With no hydrated root the replies still name the platform, so the header
    // reads "Thread on Instagram" rather than a generic fallback.
    const serviceName =
        thread?.root?.socialMediaServiceName ?? thread?.replies[0]?.socialMediaServiceName
    const platformName = serviceName ? getPlatformDisplayName(serviceName) : 'the platform'
    const accountName =
        thread?.root?.account?.accountName ?? thread?.replies[0]?.account?.accountName
    // The server can return replies without a root it could not hydrate. Show
    // each reply as its own top-level node rather than dropping them, which
    // would leave the moderator with the opened comment missing.
    const trees: ThreadNode[] = !thread
        ? []
        : thread.root
          ? [buildThread(thread.root, thread.replies)]
          : thread.replies.map((content) => ({ content, children: [], depth: 0 }))
    const commentCount = thread ? thread.replies.length + (thread.root ? 1 : 0) : 0
    // The drawer shows one thread, not the whole post, so the count says so: set against the
    // post's total when the platform gave one, otherwise scoped to "this thread". A total below
    // the thread's own count is a stale platform figure, so it falls back too.
    const postTotal = thread?.post?.commentCount
    const threadCountLabel =
        postTotal !== null && postTotal !== undefined && postTotal > commentCount
            ? `${commentCount} of ${postTotal.toLocaleString()} comments on this post`
            : `${commentCount} ${commentCount === 1 ? 'comment' : 'comments'} in this thread`
    const hidden = thread?.replies.filter((r) =>
        isActionApplied(props.actionCacheMap.get(r.id) ?? [], ContentAction.Hide)
    ).length

    return (
        <Transition show={open} as={Fragment}>
            {/* A Dialog, on SimpleModal's z-30 layer: it covers the app rail, and Headless UI
                stacks dialogs by opening order, so a reply or confirm modal opened from a drawer
                card sits above the drawer and takes Escape and outside clicks first, and a drawer
                opened from a card inside a modal sits above that modal. */}
            <Dialog className="relative z-30" onClose={close}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-ink-900/30" aria-hidden="true" />
                </TransitionChild>
                <div className="fixed inset-y-0 right-0 flex max-w-full">
                    <TransitionChild
                        as={Fragment}
                        enter="transform transition ease-out duration-300"
                        enterFrom="translate-x-full"
                        enterTo="translate-x-0"
                        leave="transform transition ease-in duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-full"
                    >
                        <DialogPanel className="w-screen max-w-[560px]" aria-label="Conversation">
                            <div className="flex h-full flex-col bg-white shadow-xl">
                                {/* The post is the drawer's heading: every comment below replies to it.
                                    Without a held post the band falls back to naming the platform. */}
                                <div className="bg-ink-800 px-5 pb-5 pt-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-xs font-medium uppercase tracking-widest text-arwen-300">
                                                {thread?.post
                                                    ? `${platformName} post`
                                                    : 'Conversation'}
                                            </div>
                                            {!thread?.post && (
                                                <h2 className="text-lg font-medium text-white">
                                                    Thread on {platformName}
                                                </h2>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={close}
                                            className="rounded-md border border-ink-600 p-1.5 text-ink-200 hover:bg-ink-700"
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </div>
                                    {thread?.post && (
                                        <ConversationPostHeader
                                            key={thread.focusContentId}
                                            post={thread.post}
                                            platformName={platformName}
                                            accountName={accountName}
                                        />
                                    )}
                                </div>
                                {thread && (
                                    <div className="border-b border-ink-200 bg-ink-50 px-5 py-2.5">
                                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-ink-700">
                                            <span>
                                                {threadCountLabel}
                                                {hidden ? ` · ${hidden} hidden` : ''}
                                            </span>
                                            {thread.canRefresh && (
                                                <button
                                                    type="button"
                                                    onClick={() => void refresh()}
                                                    disabled={refreshing}
                                                    className="inline-flex items-center gap-1 text-arwen-700 underline disabled:opacity-50"
                                                >
                                                    <ArrowPathIcon
                                                        className="h-3.5 w-3.5"
                                                        aria-hidden="true"
                                                    />
                                                    Check for new replies
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex-1 overflow-y-auto px-5 py-4">
                                    {/* Keyed on the focus comment so a previously opened thread can never linger on screen. */}
                                    <div key={thread?.focusContentId ?? 'none'}>
                                        {state === 'loading' && (
                                            <div className="mt-3">
                                                <ConversationPlaceholder
                                                    kind="loading"
                                                    platformName={platformName}
                                                />
                                            </div>
                                        )}
                                        {state === 'fetching' && (
                                            <div className="mt-3">
                                                <ConversationPlaceholder
                                                    kind="fetching"
                                                    platformName={platformName}
                                                />
                                            </div>
                                        )}
                                        {state === 'parent-unavailable' && (
                                            <div className="mt-3">
                                                <ConversationPlaceholder
                                                    kind="parent-unavailable"
                                                    platformName={platformName}
                                                    focus={thread?.root ?? undefined}
                                                />
                                            </div>
                                        )}
                                        {state === 'error' && (
                                            <p className="mt-3 text-sm text-ink-700">
                                                Couldn't load this conversation. Close the drawer
                                                and try again.
                                            </p>
                                        )}
                                        {/* Beside the held thread, never instead of it: a failed or partial fetch still leaves what Arwen holds worth acting on. The server words these for the moderator, so they show verbatim. */}
                                        {refreshError && (
                                            <p className="mt-3 text-[13px] text-ink-700">
                                                {refreshError}
                                            </p>
                                        )}
                                        {notices.map((notice, i) => (
                                            <p
                                                key={`${i}-${notice}`}
                                                className="mt-3 text-[13px] text-ink-500"
                                            >
                                                {notice}
                                            </p>
                                        ))}
                                        {thread && trees.length > 0 && (
                                            <ConversationThread
                                                trees={trees}
                                                focusContentId={thread.focusContentId}
                                                onContentAction={props.onContentAction}
                                                profileCache={props.profileCache}
                                                actionCacheMap={props.actionCacheMap}
                                                onMarkAsReadClick={props.onMarkAsReadClick}
                                                onMarkAsUnreadClick={props.onMarkAsUnreadClick}
                                            />
                                        )}
                                        {state === 'ready' &&
                                            thread &&
                                            thread.replies.length === 0 &&
                                            !thread.parentMissing && (
                                                <div className="mt-3">
                                                    <ConversationPlaceholder
                                                        kind="no-replies"
                                                        platformName={platformName}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    )
}
